
import { MeetingNote, WeeklySummary, ChatMessage } from "../types";

function extractJSON<T>(text: string): T {
  console.log("Extracting JSON from:", text);
  try {
    return JSON.parse(text);
  } catch (e) {
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        return JSON.parse(jsonMatch[0]);
      } catch (innerError) {
        console.error("JSON extraction error:", innerError);
      }
    }
    throw new Error("Could not extract valid intelligence data.");
  }
}

function formatNotesContext(notes: MeetingNote[]): string {
  return notes
    .map(n => `[SOURCE_ID: ${n.id}] [TYPE: ${n.source.toUpperCase()}] [TITLE: ${n.title}] [DATE: ${n.date}]\nCONTENT:\n${n.content}\n---`)
    .join("\n\n");
}

function getToken(): string {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('meetingbot_access_token') || '';
  }
  return '';
}

export async function summarizeMeetings(notes: MeetingNote[]): Promise<WeeklySummary> {
  if (notes.length === 0) throw new Error("No data provided for analysis.");

  const combinedContent = formatNotesContext(notes);
  console.log("Summarizing meetings with context length:", combinedContent.length);

  const prompt = `
    You are an Executive Intelligence Engine. Analyze the following workspace items and produce a strategic report.
    
    ITEMS:
    ${combinedContent}

    REQUIRED JSON FORMAT:
    {
      "conciseOverview": "1-2 paragraphs. Use Markdown for bolding key terms. DO NOT include Key Outcomes or Tasks here.",
      "detailedOverview": "Deep strategic dive. Use Markdown (headers, bullet points, bolding) to make it highly readable and professional. DO NOT include sections for Key Outcomes or Tasks here, as they are handled separately.",
      "keyOutcomes": ["list item"],
      "tasksPerPerson": [{"name": "Name (include email if available, e.g., John Doe <john@example.com> or just john@example.com)", "tasks": ["task"]}],
      "topThemes": ["theme"],
      "risksAndBlockers": ["list of critical risks, bottlenecks, or blockers identified in the items"],
      "executiveQA": [{"question": "What was the biggest decision made?", "answer": "The team decided to..."}],
      "timelineEvents": [{"date": "YYYY-MM-DD or relative time like 'Next Tuesday'", "title": "Event Name", "description": "Brief details", "type": "meeting|deadline|milestone|decision"}],
      "suggestedFollowUps": ["3 specific, intelligent questions the user might want to ask about these specific items"],
      "mindMap": {
        "nodes": [
          {"id": "Node Name", "group": "theme|person|task|project", "radius": 10}
        ],
        "links": [
          {"source": "Source Node ID", "target": "Target Node ID", "value": 1, "label": "relationship description"}
        ]
      },
      "infographic": {
        "title": "Workspace Overview",
        "stats": [
          {"label": "Total Tasks", "value": 12},
          {"label": "Key Decisions", "value": 3}
        ],
        "timeline": [
          {"date": "Oct 24", "event": "Project Alpha Launch"}
        ],
        "chart": {
          "title": "Tasks by Project",
          "data": [
            {"name": "Project A", "value": 5}
          ]
        }
      }
    }
    
    Rules:
    - Respond with RAW JSON ONLY.
    - Base everything ONLY on the provided items.
  `;

  try {
    const token = getToken();
    const res = await fetch('/api/gemini/summarize', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ prompt })
    });
    
    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || "Failed to summarize");
    }
    
    const response = await res.json();
    console.log("Gemini response received");
    const summary = extractJSON<WeeklySummary>(response.text || "");
    const chunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks;
    if (chunks) summary.groundingChunks = chunks as any;
    return summary;
  } catch (error: any) {
    console.error("Gemini API Error:", error);
    throw new Error(`Intelligence Synthesis Failed: ${error.message || "Unknown Error"}`);
  }
}

export async function askQuestionAboutContext(
  question: string, 
  notes: MeetingNote[], 
  chatHistory: ChatMessage[]
): Promise<string> {
  const context = formatNotesContext(notes);
  const historyString = chatHistory
    .map(m => `${m.role === 'user' ? 'Question' : 'Answer'}: ${m.text}`)
    .join('\n');

  const prompt = `
    You are the Intelligence Query Engine. Answer the user's question based EXCLUSIVELY on the workspace context provided.
    
    WORKSPACE CONTEXT:
    ${context}

    PREVIOUS CONVERSATION:
    ${historyString}

    USER QUESTION:
    ${question}

    INSTRUCTIONS:
    - If the answer is not in the context, say "I don't have enough information in the current workspace items to answer that."
    - Be precise and professional. 
    - Mention which source (e.g., "The Gmail transcript for [Meeting Title]") provided the information if applicable.
    - Use Markdown for formatting.
  `;

  try {
    const token = getToken();
    const res = await fetch('/api/gemini/ask', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ prompt })
    });

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || "Failed to ask");
    }

    const response = await res.json();
    return response.text || "I was unable to retrieve an answer.";
  } catch (error: any) {
    console.error("Gemini Ask Error:", error);
    throw new Error(`Failed to ask question: ${error.message || "Unknown Error"}`);
  }
}

export async function generateAudioFromText(text: string): Promise<string> {
  try {
    // Strip basic markdown to prevent TTS errors
    const plainText = text
      .replace(/[#*\`_\[\]]/g, '')
      .replace(/\n+/g, ' ')
      .trim();

    const token = getToken();
    const res = await fetch('/api/gemini/audio', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      },
      body: JSON.stringify({ plainText })
    });

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || "Failed to generate audio");
    }

    const response = await res.json();
    if (!response.audio) {
      throw new Error("No audio data returned from Gemini TTS.");
    }
    
    return response.audio;
  } catch (error: any) {
    console.error("Gemini TTS Error:", error);
    throw new Error(`Text-to-Speech Failed: ${error.message || "Unknown Error"}`);
  }
}
